import { doc, getDoc } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import type { CheckinInfo, MemberCheckinStatus } from '../shared/types/checkin.js'
import type { Member } from '../shared/types/member.js'
import { getMemberDuesStatus } from './useShouldPayDues.js'
import { isSpecialtyRedshirt, isFreeRangeRedshirt } from '~/shared/types/memberType.js'

/**
 * Composable to get check-in status for members
 * Handles fetching check-in data from Firestore subcollection
 */
export function useCheckinStatus() {
  const db = useFirestore()
  const { paradeYear: defaultParadeYear, isParadeDayAt } = useParadeYear()

  /**
   * Read a member's check-in for a year. Throws when it can't be read, so the
   * check-in screen can tell "couldn't load" apart from "not checked in".
   * @param memberId - UID of the member
   * @param year - Parade year (defaults to next parade year)
   * @returns CheckinInfo or null if not checked in
   */
  async function fetchCheckinStatus(
    memberId: string,
    year?: number,
  ): Promise<CheckinInfo | null> {
    const paradeYear = year ?? defaultParadeYear.value

    const checkinRef = doc(
      db,
      'members',
      memberId,
      'checkins',
      paradeYear.toString(),
    )
    const checkinSnap = await getDoc(checkinRef)

    if (!checkinSnap.exists()) {
      return null
    }

    const data = checkinSnap.data()

    return {
      year: data.year,
      checkedIn: data.checkedIn,
      timestamp: data.timestamp?.toDate(),
      checkedInBy: data.checkedInBy,
      checkedInByName: data.checkedInByName,
      method: data.method,
      captainId: data.captainId,
      captainName: data.captainName,
    }
  }

  /**
   * Get check-in status for a specific member and year
   * @param memberId - UID of the member
   * @param year - Parade year (defaults to next parade year)
   * @returns CheckinInfo, or null if not checked in or it can't be read
   */
  async function getCheckinStatus(
    memberId: string,
    year?: number,
  ): Promise<CheckinInfo | null> {
    try {
      return await fetchCheckinStatus(memberId, year)
    }
    catch (error) {
      console.error('Error fetching check-in status:', error)
      return null
    }
  }

  /**
   * Get full check-in status including payment eligibility
   * @param member - Member object
   * @param year - Parade year (defaults to next parade year)
   * @param options.throwOnReadError - throw when the check-in can't be read,
   *   instead of reporting "not checked in"
   * @returns MemberCheckinStatus with payment and ticket info
   */
  async function getMemberCheckinStatus(
    member: Member,
    year?: number,
    options: { throwOnReadError?: boolean } = {},
  ): Promise<MemberCheckinStatus> {
    const paradeYear = year ?? defaultParadeYear.value

    // Get check-in status
    const checkinInfo = options.throwOnReadError
      ? await fetchCheckinStatus(member.id, paradeYear)
      : await getCheckinStatus(member.id, paradeYear)

    // Determine if member can check in (based on payment status)
    // Use getMemberDuesStatus directly with the passed member to ensure
    // subkrewe data (including music field for band exemption) is available
    const duesStatus = getMemberDuesStatus(member)
    const hasPaid = !duesStatus.shouldPay

    // Check if member has a subkrewe
    const noSubkrewe = !member.subkrewe

    // Check if member is a specialty redshirt (Free-Range or Nerd Herder)
    // Free-Range can check in on parade day, Nerd Herders check in at Equipment Station
    const specialtyRedshirt = isSpecialtyRedshirt(member)
    // As of now: a check-in screen open since the evening before kept
    // blocking them on parade day
    const freeRangeBlocked = isFreeRangeRedshirt(member) && !isParadeDayAt(new Date())

    // Count VIP, parking, and children tickets for the year
    const yearPurchases = member.purchases?.[paradeYear]

    // Check if member has unpaid children
    // Sponsored subkrewe members and band members are exempt from children's ticket requirement
    const isSponsored = member.subkrewe?.sponsored === true
    // Only an approved band, as for dues (shared/dues/status.ts)
    const isBand = member.subkrewe?.music === 'band' && member.subkrewe?.approved === 'Yes'
    const childrenObj = member.children || {}
    const totalChildrenCount = Object.keys(childrenObj).length
    const childrenPurchases = yearPurchases?.children || []
    // Count unique childrenIds across all completed children purchase records
    const paidChildrenIds = new Set<string>()
    for (const purchase of childrenPurchases) {
      if (purchase.status === 'completed' && Array.isArray(purchase.childrenIds)) {
        for (const id of purchase.childrenIds) paidChildrenIds.add(id)
      }
    }
    const paidChildrenCount = paidChildrenIds.size
    const hasUnpaidChildren = !isSponsored && !isBand && totalChildrenCount > 0 && paidChildrenCount < totalChildrenCount

    // Can only check in if paid AND has a subkrewe AND not blocked specialty redshirt AND no unpaid children
    // Free-Range redshirts blocked except on parade day
    const canCheckIn = hasPaid && !noSubkrewe && !freeRangeBlocked && !hasUnpaidChildren
    const vipCount = yearPurchases?.vip?.filter(
      p => p.status === 'completed',
    ).length ?? 0
    const parkingCount = yearPurchases?.parking?.filter(
      p => p.status === 'completed',
    ).length ?? 0
    // For children count: use purchased ticket count, but for exempt members (band/sponsored)
    // use the actual children count since they don't need to purchase tickets
    const purchasedChildrenCount = yearPurchases?.children?.reduce(
      (sum, purchase) => sum + (purchase.status === 'completed' ? (purchase.quantity ?? 0) : 0),
      0,
    ) ?? 0
    const isChildrenExempt = isSponsored || isBand
    const childrenCount = isChildrenExempt ? totalChildrenCount : purchasedChildrenCount

    return {
      member,
      canCheckIn,
      hasPaid,
      noSubkrewe,
      isSpecialtyRedshirt: specialtyRedshirt,
      hasUnpaidChildren,
      checkinInfo,
      vipCount,
      childrenCount,
      parkingCount,
    }
  }

  /**
   * Get check-in status for multiple members
   * Useful for captain bulk check-in interface
   * @param members - Array of members
   * @param year - Parade year (defaults to current parade year)
   * @returns Array of MemberCheckinStatus
   */
  async function getBulkCheckinStatus(
    members: Member[],
    year?: number,
  ): Promise<MemberCheckinStatus[]> {
    const statusPromises = members.map(member =>
      getMemberCheckinStatus(member, year),
    )
    return Promise.all(statusPromises)
  }

  return {
    fetchCheckinStatus,
    getCheckinStatus,
    getMemberCheckinStatus,
    getBulkCheckinStatus,
  }
}
